/**
 * Frontend Logging Service for Vue.js
 * Provides structured logging for the frontend application
 */

class Logger {
    constructor() {
        this.logBuffer = []
        this.batchSize = 10
        this.flushInterval = 5000 // 5 seconds
        this.endpoint = '/api/logs/frontend'
        this.isEnabled = true // Can be disabled if auth fails consistently
        this.failureCount = 0
        this.maxFailures = 3 // Disable after 3 consecutive failures
        
        // Start periodic flush
        this.startPeriodicFlush()
        
        // Flush on page unload
        window.addEventListener('beforeunload', () => {
            this.flush(true) // Synchronous flush on unload
        })
    }

    /**
     * Log an error with context
     */
    error(message, context = {}, component = 'general') {
        this.log('error', message, context, component)
        
        // Also log to console in development
        if (import.meta.env.DEV) {
            console.error(`[${component}] ${message}`, context)
        }
    }

    /**
     * Log a warning
     */
    warn(message, context = {}, component = 'general') {
        this.log('warning', message, context, component)
        
        if (import.meta.env.DEV) {
            console.warn(`[${component}] ${message}`, context)
        }
    }

    /**
     * Log general information
     */
    info(message, context = {}, component = 'general') {
        this.log('info', message, context, component)
        
        if (import.meta.env.DEV) {
            console.info(`[${component}] ${message}`, context)
        }
    }

    /**
     * Log debug information (only in development)
     */
    debug(message, context = {}, component = 'general') {
        if (import.meta.env.DEV) {
            console.debug(`[${component}] ${message}`, context)
        }
    }

    /**
     * Log API calls and responses
     */
    logApiCall(endpoint, request = {}, response = {}, statusCode = 200, duration = 0) {
        const context = {
            endpoint,
            request_data: request,
            response_data: response,
            status_code: statusCode,
            duration_ms: duration,
            url: window.location.href,
            user_agent: navigator.userAgent
        }

        const level = statusCode >= 400 ? 'error' : (statusCode >= 300 ? 'warning' : 'info')
        this.log(level, `API Call: ${endpoint}`, context, 'api')
    }

    /**
     * Log user interactions
     */
    logUserAction(action, context = {}) {
        this.log('info', `User Action: ${action}`, {
            ...context,
            url: window.location.href,
            timestamp: new Date().toISOString()
        }, 'user_interaction')
    }

    /**
     * Log Vue component errors
     */
    logVueError(error, component, info) {
        this.log('error', `Vue Component Error in ${component}`, {
            error_message: error.message,
            error_stack: error.stack,
            component_info: info,
            url: window.location.href
        }, 'vue')
    }

    /**
     * Log route changes
     */
    logRouteChange(from, to) {
        this.log('info', 'Route Change', {
            from: from?.fullPath || 'unknown',
            to: to?.fullPath || 'unknown',
            timestamp: new Date().toISOString()
        }, 'router')
    }

    /**
     * Log performance issues
     */
    logPerformance(operation, duration, context = {}) {
        if (duration > 1000) { // Log operations taking more than 1 second
            this.log('warning', `Slow Operation: ${operation}`, {
                ...context,
                duration_ms: duration,
                url: window.location.href
            }, 'performance')
        }
    }

    /**
     * Core logging method
     */
    log(level, message, context = {}, component = 'general') {
        const logEntry = {
            level,
            message,
            component,
            context: {
                ...context,
                timestamp: new Date().toISOString(),
                url: window.location.href,
                user_agent: navigator.userAgent,
                screen_resolution: `${screen.width}x${screen.height}`,
                viewport_size: `${window.innerWidth}x${window.innerHeight}`
            }
        }

        this.logBuffer.push(logEntry)

        // Flush immediately for errors
        if (level === 'error') {
            this.flush()
        } else if (this.logBuffer.length >= this.batchSize) {
            this.flush()
        }
    }

    /**
     * Send logs to backend
     */
    async flush(synchronous = false) {
        if (this.logBuffer.length === 0 || !this.isEnabled) return

        const logs = [...this.logBuffer]
        this.logBuffer = []

        try {
            if (synchronous && navigator.sendBeacon) {
                // Use sendBeacon for synchronous sending (page unload)
                // Note: sendBeacon doesn't support auth headers easily
                navigator.sendBeacon(
                    this.endpoint,
                    new Blob([JSON.stringify({ logs })], { type: 'application/json' })
                )
            } else {
                // Use axios for normal async sending
                const axios = window.axios
                if (axios) {
                    // Create custom axios instance without baseURL to avoid URL conflicts
                    const customAxios = axios.create({
                        baseURL: '',
                        timeout: 5000,
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Requested-With': 'XMLHttpRequest'
                        }
                    })
                    
                    await customAxios.post(this.endpoint, { logs })
                    this.failureCount = 0 // Reset failure count on success
                } else {
                    // Fallback to fetch if axios is not available
                    const response = await fetch(this.endpoint, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Requested-With': 'XMLHttpRequest'
                        },
                        body: JSON.stringify({ logs })
                    })
                    
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
                    }
                    this.failureCount = 0 // Reset failure count on success
                }
            }
        } catch (error) {
            this.handleFlushError(error, logs)
        }
    }

    /**
     * Handle flush errors with backoff strategy
     */
    handleFlushError(error, logs) {
        this.failureCount++
        
        // Only log to console to avoid infinite loops
        if (import.meta.env.DEV) {
            console.warn(`Logger: Failed to send logs (attempt ${this.failureCount}/${this.maxFailures}):`, error.message)
        }
        
        // Disable logging after too many failures to avoid spam
        if (this.failureCount >= this.maxFailures) {
            this.isEnabled = false
            console.warn('Logger: Disabled frontend logging due to repeated failures. Check authentication or network connectivity.')
            return
        }
        
        // For auth errors (401), don't retry immediately
        if (error.response?.status === 401 || error.message.includes('401')) {
            if (import.meta.env.DEV) {
                console.warn('Logger: Authentication required for logging. Logs will be stored locally only.')
            }
            return // Don't add logs back to buffer for auth errors
        }
        
        // For other errors, add logs back to buffer for retry (but limit buffer size)
        if (this.logBuffer.length < 50) { // Prevent memory issues
            this.logBuffer.unshift(...logs)
        }
    }

    /**
     * Get authorization header if available
     */
    getAuthHeader() {
        const token = localStorage.getItem('auth_token')
        return token ? `Bearer ${token}` : ''
    }

    /**
     * Re-enable logging (useful after authentication)
     */
    enable() {
        this.isEnabled = true
        this.failureCount = 0
        if (import.meta.env.DEV) {
            console.info('Logger: Frontend logging re-enabled')
        }
    }

    /**
     * Disable logging
     */
    disable() {
        this.isEnabled = false
        this.logBuffer = [] // Clear buffer
        if (import.meta.env.DEV) {
            console.info('Logger: Frontend logging disabled')
        }
    }

    /**
     * Check if logging is enabled
     */
    get enabled() {
        return this.isEnabled
    }

    /**
     * Start periodic flushing
     */
    startPeriodicFlush() {
        setInterval(() => {
            this.flush()
        }, this.flushInterval)
    }

    /**
     * Create a performance timer
     */
    startTimer(label) {
        const startTime = performance.now()
        
        return {
            end: (context = {}) => {
                const duration = performance.now() - startTime
                this.logPerformance(label, duration, context)
                return duration
            }
        }
    }

    /**
     * Log application lifecycle events
     */
    logLifecycleEvent(event, context = {}) {
        this.log('info', `Lifecycle: ${event}`, {
            ...context,
            timestamp: new Date().toISOString(),
            memory_usage: this.getMemoryUsage(),
            connection_type: this.getConnectionType()
        }, 'lifecycle')
    }

    /**
     * Log security events
     */
    logSecurityEvent(event, context = {}) {
        this.log('warning', `Security: ${event}`, {
            ...context,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            referrer: document.referrer,
            user_agent: navigator.userAgent
        }, 'security')
    }

    /**
     * Get memory usage information
     */
    getMemoryUsage() {
        if (performance.memory) {
            return {
                used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024),
                total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024),
                limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024)
            }
        }
        return null
    }

    /**
     * Get connection type
     */
    getConnectionType() {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
        if (connection) {
            return {
                type: connection.effectiveType,
                downlink: connection.downlink,
                rtt: connection.rtt
            }
        }
        return null
    }

    /**
     * Log email campaign metrics
     */
    logEmailMetrics(action, data = {}) {
        this.log('info', `Email: ${action}`, {
            ...data,
            timestamp: new Date().toISOString()
        }, 'email_metrics')
    }

    /**
     * Log subscription events
     */
    logSubscriptionEvent(event, planData = {}) {
        this.log('info', `Subscription: ${event}`, {
            ...planData,
            timestamp: new Date().toISOString()
        }, 'subscription')
    }

    /**
     * Log admin actions
     */
    logAdminAction(action, context = {}) {
        this.log('warning', `Admin: ${action}`, {
            ...context,
            timestamp: new Date().toISOString(),
            admin_user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).email : 'unknown'
        }, 'admin')
    }
}

// Create singleton instance
const logger = new Logger()

// Setup global error handling
window.addEventListener('error', (event) => {
    logger.error('Uncaught JavaScript Error', {
        message: event.message,
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
        stack: event.error?.stack
    }, 'javascript')
})

window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled Promise Rejection', {
        reason: event.reason?.toString() || 'Unknown',
        stack: event.reason?.stack
    }, 'promise')
})

export default logger